import { Link } from "react-router-dom";
import { Heart, ChevronRight, Sparkles as SparklesIcon } from "lucide-react";
import { IMAGES, CATEGORIES, FEATURES } from "@/data/boutique";

/* ===== Custom detailed ornate icons for the feature strip ===== */
const ICON_PROPS = {
    width: 44,
    height: 44,
    viewBox: "0 0 64 64",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.4,
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

const OrnateCrown = (p) => (
    <svg {...ICON_PROPS} {...p}>
        <path d="M10 38 L16 22 L24 32 L32 18 L40 32 L48 22 L54 38 Z" />
        <path d="M10 38 H54 V44 H10 Z" />
        <circle cx="16" cy="22" r="2" fill="currentColor" />
        <circle cx="32" cy="18" r="2.4" fill="currentColor" />
        <circle cx="48" cy="22" r="2" fill="currentColor" />
        <path d="M14 48 H50" strokeWidth="0.8" opacity="0.6" />
        <path d="M22 52 H42" strokeWidth="0.8" opacity="0.4" />
    </svg>
);
const OrnateCross = (p) => (
    <svg {...ICON_PROPS} {...p}>
        <path d="M28 10 H36 V24 H50 V32 H36 V54 H28 V32 H14 V24 H28 Z" />
        <path d="M32 14 V20 M32 36 V48 M20 28 H26 M38 28 H44" strokeWidth="0.8" opacity="0.55" />
        <path d="M32 8 Q34 6 32 4 Q30 6 32 8" />
    </svg>
);
const OrnateTruck = (p) => (
    <svg {...ICON_PROPS} {...p}>
        <path d="M6 22 H38 V44 H6 Z" />
        <path d="M38 28 H50 L56 36 V44 H38 Z" />
        <circle cx="16" cy="46" r="4" />
        <circle cx="46" cy="46" r="4" />
        <circle cx="16" cy="46" r="1.5" fill="currentColor" />
        <circle cx="46" cy="46" r="1.5" fill="currentColor" />
        <path d="M10 28 H30 M10 34 H26" strokeWidth="0.8" opacity="0.55" />
        <path d="M42 32 L52 32" strokeWidth="0.8" opacity="0.55" />
    </svg>
);
const OrnateHeart = (p) => (
    <svg {...ICON_PROPS} {...p}>
        <path d="M32 50 C18 40 10 32 10 22 C10 16 14 12 20 12 C25 12 29 15 32 20 C35 15 39 12 44 12 C50 12 54 16 54 22 C54 32 46 40 32 50 Z" />
        <path d="M32 24 C30 22 27 22 26 25 C26 28 30 30 32 32 C34 30 38 28 38 25 C37 22 34 22 32 24 Z" opacity="0.7" />
        <circle cx="20" cy="18" r="1" fill="currentColor" />
        <circle cx="44" cy="18" r="1" fill="currentColor" />
    </svg>
);
const OrnateBadge = (p) => (
    <svg {...ICON_PROPS} {...p}>
        <path d="M32 6 L38 12 L46 11 L48 19 L55 22 L52 30 L55 38 L48 41 L46 49 L38 48 L32 54 L26 48 L18 49 L16 41 L9 38 L12 30 L9 22 L16 19 L18 11 L26 12 Z" />
        <path d="M22 30 L29 37 L42 24" strokeWidth="2" />
        <circle cx="32" cy="30" r="14" strokeWidth="0.7" opacity="0.4" />
    </svg>
);

const ORNATE_ICON_MAP = {
    Crown: OrnateCrown,
    Cross: OrnateCross,
    Truck: OrnateTruck,
    Heart: OrnateHeart,
    BadgeCheck: OrnateBadge,
};

function Sparkles() {
    return (
        <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 800 600"
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            {Array.from({ length: 22 }).map((_, i) => {
                const x = (i * 47) % 800;
                const y = ((i * 73) % 580) + 10;
                const r = 1 + (i % 3) * 0.7;
                return (
                    <circle
                        key={i}
                        cx={x}
                        cy={y}
                        r={r}
                        fill="#fff"
                        opacity={0.35 + ((i % 4) * 0.12)}
                        className="twinkle"
                        style={{ animationDelay: `${(i % 7) * 0.5}s` }}
                    />
                );
            })}
        </svg>
    );
}

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* ===== HERO – full-bleed banner with text overlay ===== */}
            <section
                className="relative overflow-hidden"
                aria-label="Beyond Blessed hero banner"
                data-testid="hero-section"
            >
                {/* Background image fills entire hero */}
                <div className="absolute inset-0">
                    <img
                        src={IMAGES.hero}
                        alt="Stylish western woman wearing a cowboy hat and black fringe top with turquoise jewelry, posed with brown cowboy boots and a blush gift bag — Beyond Blessed Gifts & More LLC"
                        className="w-full h-full object-cover object-[88%_center] sm:object-[75%_center] lg:object-right hero-img-ken"
                        loading="eager"
                        fetchPriority="high"
                    />
                    {/* Soft cream gradient on left so text reads cleanly — covers more on mobile to keep text off the woman */}
                    <div className="absolute inset-0 bg-gradient-to-r from-bb-cream via-bb-cream/95 sm:via-bb-cream/85 lg:via-bb-cream/75 to-transparent" />
                    <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-b from-white/30 via-transparent to-white/20 pointer-events-none" />
                </div>

                {/* Sparkles overlay */}
                <Sparkles />

                {/* Content overlay */}
                <div className="relative z-10 max-w-[1400px] mx-auto pl-12 pr-6 sm:pl-20 sm:pr-10 lg:pl-32 lg:pr-12 py-16 lg:py-24 min-h-[560px] sm:min-h-[640px] lg:min-h-[760px] flex flex-col justify-center">
                    <div className="max-w-[92%] sm:max-w-md lg:max-w-xl hero-text-clear">
                        <h1
                            className="hero-beyond hero-rise text-5xl sm:text-7xl lg:text-[8rem] xl:text-[9rem] text-bb-text leading-[0.95]"
                            data-testid="hero-h1"
                        >
                            BEYOND
                        </h1>

                        <div className="relative mt-2 sm:mt-1 lg:mt-2 hero-rise d-1">
                            <span className="hero-blessed text-6xl sm:text-8xl lg:text-[8rem] xl:text-[10rem] inline-block leading-[0.95]">
                                Blessed
                            </span>
                        </div>

                        <div className="mt-5 flex items-center gap-3 hero-rise d-2">
                            <span className="swoosh w-10 sm:w-16" />
                            <span className="font-serif-display tracking-[0.3em] text-[0.7rem] sm:text-xs text-bb-text font-semibold">
                                GIFTS &nbsp;&amp;&nbsp; MORE&nbsp; LLC
                            </span>
                            <span className="swoosh w-10 sm:w-16" />
                        </div>

                        <p className="mt-6 font-serif-display tracking-[0.36em] text-[0.7rem] sm:text-xs text-bb-text font-semibold hero-rise d-3">
                            FAITH. &nbsp; FASHION. &nbsp; STRENGTH.
                        </p>
                        <p className="mt-2 font-script text-3xl sm:text-4xl text-bb-pink-script hero-rise d-3" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.8)" }}>
                            Wear Your Blessings
                        </p>

                        <div className="mt-7 hero-rise d-4">
                            <Link
                                to="/shop"
                                className="btn-black btn-shine"
                                data-testid="hero-shop-now"
                            >
                                Shop Now
                                <Heart size={14} className="fill-current text-bb-pink-script animate-heart-beat" />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Floating "Blessed" chip — sits over the photo on the right */}
                <div className="hidden sm:flex absolute bottom-6 right-8 lg:right-16 bg-bb-blush text-bb-warm-brown px-4 py-2 rounded-full shadow-lg items-center gap-2 gentle-float z-10">
                    <Heart size={14} className="fill-current text-bb-pink-script" />
                    <span className="font-script text-lg">Blessed</span>
                </div>
            </section>

            {/* ===== FEATURE STRIP — ornate icons ===== */}
            <section
                className="bg-bb-black text-white relative overflow-hidden"
                data-testid="feature-strip"
                aria-label="Boutique features"
            >
                {/* subtle gold lines */}
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-bb-rose-gold/40 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-bb-rose-gold/40 to-transparent" />

                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-10">
                    {FEATURES.map((f, i) => {
                        const Icon = ORNATE_ICON_MAP[f.icon];
                        return (
                            <div
                                key={f.key}
                                className="text-center fade-up feature-card"
                                style={{ transitionDelay: `${i * 90}ms` }}
                                data-testid={`feature-${f.key}`}
                            >
                                {/* Ornate medallion */}
                                <div className="mx-auto relative w-[88px] h-[88px] flex items-center justify-center feature-medallion">
                                    {/* outer thin ring */}
                                    <svg
                                        className="absolute inset-0 w-full h-full feature-ring-spin"
                                        viewBox="0 0 100 100"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="46"
                                            fill="none"
                                            stroke="#c08a7a"
                                            strokeWidth="0.6"
                                            strokeDasharray="2 4"
                                            opacity="0.7"
                                        />
                                    </svg>
                                    {/* inner solid ring */}
                                    <div className="absolute inset-2 rounded-full border border-bb-rose-gold/55" />
                                    {/* corner ornaments */}
                                    <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-bb-rose-gold" />
                                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-bb-rose-gold" />
                                    <span className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-bb-rose-gold" />
                                    <span className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-bb-rose-gold" />
                                    {/* icon */}
                                    <span className="text-bb-rose-gold relative z-10 feature-icon">
                                        <Icon />
                                    </span>
                                </div>

                                <p className="mt-4 font-serif-display tracking-[0.22em] text-[0.78rem] uppercase text-bb-blush">
                                    {f.title}
                                </p>
                                <p className="mt-2 text-[0.78rem] text-white/75 leading-snug max-w-[180px] mx-auto">
                                    {f.text}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ===== SHOP BY CATEGORY ===== */}
            <section
                className="cream-textured relative py-16 md:py-24 overflow-hidden"
                data-testid="category-section"
                aria-label="Shop by category"
            >
                {/* Faded curved leopard accents behind the category cards */}
                <div className="leopard-blob leopard-blob--left" aria-hidden="true" />
                <div className="leopard-blob leopard-blob--right" aria-hidden="true" />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-center fade-up">
                        <div className="flex items-center justify-center gap-3 text-bb-rose-gold">
                            <span className="swoosh w-12" />
                            <Heart size={14} className="fill-current text-bb-pink-script animate-heart-beat" />
                            <span className="swoosh w-12" />
                        </div>
                        <h2
                            className="font-serif-display text-3xl md:text-5xl tracking-[0.18em] uppercase text-bb-text mt-3"
                            data-testid="category-heading"
                        >
                            Shop By Category
                        </h2>
                        <p className="mt-3 font-serif-soft italic text-bb-text-soft text-lg">
                            Curated faith-based & western boutique collections
                        </p>
                    </div>

                    <div className="mt-12 md:mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-7">
                        {CATEGORIES.map((c, i) => (
                            <article
                                key={c.slug}
                                className="category-card fade-up"
                                style={{ transitionDelay: `${i * 80}ms` }}
                                data-testid={`category-card-${c.slug}`}
                            >
                                <div className="arch-img">
                                    <img src={c.image} alt={c.alt} loading="lazy" />
                                </div>
                                <div className="px-3 py-5 text-center">
                                    <h3 className="font-serif-display tracking-[0.22em] uppercase text-sm md:text-base text-bb-text">
                                        {c.title}
                                    </h3>
                                    <div className="mt-3 flex justify-center">
                                        <Link
                                            to={`/shop?collection=${c.slug}`}
                                            data-testid={`category-cta-${c.slug}`}
                                            className="btn-blush"
                                        >
                                            Shop Now
                                        </Link>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== ABOUT (no family image) + PORCH PICKUP ===== */}
            <section
                className="cream-textured relative py-16 md:py-24 sparkle-overlay"
                data-testid="about-section"
                aria-label="About Ashley and porch pickup"
            >
                <div className="max-w-[1300px] mx-auto px-6 lg:px-10 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
                    {/* Center text */}
                    <div className="text-center flex flex-col justify-center fade-up bg-white/40 backdrop-blur-sm rounded-md border border-bb-warm-brown/20 p-8 md:p-10">
                        <div className="mx-auto mb-4">
                            <SparklesIcon size={20} className="text-bb-pink-script animate-spin-slow" />
                        </div>
                        <h2
                            className="font-serif-display tracking-[0.22em] uppercase text-2xl md:text-3xl text-bb-text"
                            data-testid="about-heading"
                        >
                            Hi, I'm Ashley
                        </h2>
                        <div className="mt-3 flex items-center justify-center gap-3 text-bb-rose-gold">
                            <span className="swoosh w-10" />
                            <Heart size={14} className="fill-current text-bb-pink-script animate-heart-beat" />
                            <span className="swoosh w-10" />
                        </div>
                        <p className="mt-6 text-bb-text-soft leading-relaxed">
                            I'm a proud mama to triplets and the heart behind{" "}
                            <span className="font-script text-bb-pink-script text-xl">
                                Beyond Blessed
                            </span>{" "}
                            Gifts & More LLC.
                        </p>
                        <p className="mt-3 text-bb-text-soft leading-relaxed">
                            What started as a passion for creating and gifting has grown into a boutique built on faith, family, and purpose.
                        </p>
                        <p className="mt-3 text-bb-text-soft leading-relaxed">
                            Every item is handpicked with love to inspire, encourage, and remind you that you are truly{" "}
                            <span className="font-serif-display italic">Beyond Blessed.</span>
                        </p>
                        <div className="mt-7">
                            <Link
                                to="/about"
                                className="btn-blush"
                                data-testid="about-cta"
                            >
                                Learn More About Us
                                <ChevronRight size={14} />
                            </Link>
                        </div>
                    </div>

                    {/* Porch pickup box */}
                    <div className="fade-up">
                        <div className="h-full rounded-md border border-bb-warm-brown/30 bg-bb-blush-2 p-7 lg:p-10 relative overflow-hidden shadow-lg shadow-bb-warm-brown/15 porch-card">
                            <div className="absolute top-3 right-3 w-12 h-12 rounded-full bg-white/70 flex items-center justify-center gentle-float">
                                <OrnateTruck className="text-bb-rose-gold" width="22" height="22" />
                            </div>
                            <h3 className="font-serif-display tracking-[0.18em] uppercase text-xl md:text-2xl text-bb-text">
                                Porch Pickup
                                <br />
                                Available!
                            </h3>
                            <div className="mt-3 swoosh w-16" />
                            <p className="mt-5 text-bb-text-soft leading-relaxed">
                                We offer convenient porch pickup for our local customers.
                            </p>
                            <p className="mt-3 text-bb-text-soft leading-relaxed">
                                Simply choose{" "}
                                <span className="font-medium text-bb-warm-brown-2">
                                    "Porch Pickup"
                                </span>{" "}
                                at checkout and we'll take care of the rest!
                            </p>
                            <div className="mt-6">
                                <Link
                                    to="/contact"
                                    className="btn-outline-brown"
                                    data-testid="porch-pickup-cta"
                                >
                                    Learn More
                                </Link>
                            </div>
                            <Heart
                                size={90}
                                className="absolute -bottom-5 -right-3 text-bb-pink-script/15 fill-current pointer-events-none animate-heart-beat-slow"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== PAYMENT OPTIONS ===== */}
            <section
                className="bg-bb-cream-2 py-14"
                data-testid="payment-section"
                aria-label="Accepted payment options"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
                    <div className="fade-up">
                        <h3 className="font-serif-display tracking-[0.18em] uppercase text-xl md:text-2xl text-bb-text">
                            We Accept
                        </h3>
                        <p className="font-script text-3xl text-bb-pink-script mt-1">
                            Multiple Payment Options
                        </p>
                        <div className="mt-3 flex items-center justify-center gap-3 text-bb-rose-gold">
                            <span className="swoosh w-12" />
                            <Heart size={12} className="fill-current text-bb-pink-script animate-heart-beat" />
                            <span className="swoosh w-12" />
                        </div>
                    </div>

                    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { name: "Venmo", handle: "@BeyondBlessedGiftsandMoreLLC", bg: "#3D95CE", letter: "V" },
                            { name: "PayPal", handle: "Beyond.03.blessed@gmail.com", bg: "#003087", letter: "P" },
                            { name: "Cash App", handle: "$BeyondBlessedGifts", bg: "#00C244", letter: "$" },
                            { name: "Debit Card", handle: "Visa • Mastercard • Discover • Amex", bg: "#0a0a0a", letter: "▭", isCard: true },
                        ].map((p, i) => (
                            <div
                                key={p.name}
                                className="flex items-center gap-3 px-4 py-3 bg-white rounded-md border border-bb-warm-brown/20 shadow-sm fade-up hover:-translate-y-1 hover:shadow-md transition-all duration-500"
                                style={{ transitionDelay: `${i * 80}ms` }}
                                data-testid={`payment-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div
                                    className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold shrink-0"
                                    style={{ background: p.bg }}
                                >
                                    <span className="text-base">{p.letter}</span>
                                </div>
                                <div className="text-left">
                                    <p className="font-serif-display tracking-[0.18em] uppercase text-xs text-bb-text">
                                        {p.name}
                                    </p>
                                    <p className="text-[0.7rem] text-bb-text-soft mt-0.5">
                                        {p.handle}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
